import type {
  BrandingPresetConfigurationItem,
  CommunityBranding,
  CommunityBrandingColorTokens,
  CommunityBrandingTypographyTokens,
} from '@/services/types/api.types';

export const defaultBrandingPresetKey = 'default';

const fallbackBrandingColors: Required<CommunityBrandingColorTokens> = {
  bg: '#ffffff',
  surface: '#f5f5f4',
  surface_raised: '#e7e5e4',
  surface_hover: '#e7e5e4',
  surface_active: '#d6d3d1',
  text: '#1c1917',
  muted: '#57534e',
  text_subtle: '#78716c',
  disabled: '#1c1917',
  brand: '#005179',
  brand_contrast: '#fafaf9',
  link: '#005179',
  link_hover: '#003a56',
  border: '#1c1917',
  border_strong: '#1c1917',
  focus_ring: '#005179',
  success: '#15803d',
  success_bg: '#dcfce7',
  warning: '#a16207',
  warning_bg: '#fef3c7',
  danger: '#b91c1c',
  danger_bg: '#fee2e2',
  info: '#0369a1',
  info_bg: '#e0f2fe',
};

const fallbackBrandingTypography: Required<CommunityBrandingTypographyTokens> = {
  font_size_display: 'clamp(2.25rem, 3vw + 1rem, 3.25rem)',
  font_size_headline: 'clamp(1.55rem, 1.4vw + 1.2rem, 2.15rem)',
  font_size_title: '1.25rem',
  font_size_body: '1rem',
  font_size_small: '0.875rem',
  leading_display: '1.05',
  leading_headline: '1.15',
  leading_body: '1.6',
  font_size_body_mobile: null,
  leading_body_mobile: null,
  tracking_display: '-0.02em',
  tracking_headline: '-0.01em',
};

export const fallbackBrandingPreset: BrandingPresetConfigurationItem = {
  key: defaultBrandingPresetKey,
  label: {
    en: 'Default',
    fr: 'Par défaut',
  },
  tokens: {
    colors: fallbackBrandingColors,
    typography: fallbackBrandingTypography,
  },
  fonts: {
    heading: {
      source: 'remote-css',
      family: "'Libre Caslon Text'",
      css_url:
        'https://fonts.googleapis.com/css2?family=Libre+Caslon+Text:wght@400;700&family=Poppins:wght@400;500;600&display=swap',
      fallback: 'serif',
    },
    body: {
      source: 'remote-css',
      family: "'Poppins'",
      css_url:
        'https://fonts.googleapis.com/css2?family=Libre+Caslon+Text:wght@400;700&family=Poppins:wght@400;500;600&display=swap',
      fallback: 'sans-serif',
    },
  },
};

export function getBrandingPresets(
  presets: BrandingPresetConfigurationItem[] | null | undefined,
): BrandingPresetConfigurationItem[] {
  if (!Array.isArray(presets) || presets.length === 0) {
    return [fallbackBrandingPreset];
  }

  return presets;
}

export function resolveBrandingPreset(
  branding: CommunityBranding | null | undefined,
  presets: BrandingPresetConfigurationItem[] | null | undefined,
  fallbackPresetKey?: string | null,
): BrandingPresetConfigurationItem {
  const availablePresets = getBrandingPresets(presets);
  const requestedKey = branding?.preset_key ?? fallbackPresetKey;

  return (
    availablePresets.find((preset) => preset.key === requestedKey) ??
    availablePresets.find((preset) => preset.key === defaultBrandingPresetKey) ??
    fallbackBrandingPreset
  );
}
